/** Hex coordinate math — axial (q, r) with odd-r offset layout. */

export interface HexCoord {
  readonly q: number;
  readonly r: number;
}

export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

export interface CubeCoord {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

// sqrt(3) ≈ 1.732
const SQRT3 = 1.732;

/** Convert an axial hex coordinate to a pixel position. Odd rows are shifted right by half a cell. */
export function hexToPixel(coord: HexCoord, cellSize: number): Vec2 {
  const x = cellSize * (coord.q * 2 + (coord.r & 1));
  const y = cellSize * coord.r * SQRT3;
  return { x, y };
}

/** Convert offset (odd-r) coordinates to cube coordinates. */
export function offsetToCube(coord: HexCoord): CubeCoord {
  const x = coord.q - ((coord.r - (coord.r & 1)) / 2);
  const z = coord.r;
  const y = -x - z;
  return { x, y, z };
}

/** Convert cube coordinates back to offset (odd-r) coordinates. */
export function cubeToOffset(cube: CubeCoord): HexCoord {
  const q = cube.x + ((cube.z - (cube.z & 1)) / 2);
  const r = cube.z;
  return { q, r };
}

/** Round fractional cube coordinates to the nearest integer cube coordinate. */
function cubeRound(fx: number, fy: number, fz: number): CubeCoord {
  let rx = Math.round(fx);
  let ry = Math.round(fy);
  let rz = Math.round(fz);

  const dx = Math.abs(rx - fx);
  const dy = Math.abs(ry - fy);
  const dz = Math.abs(rz - fz);

  if (dx > dy && dx > dz) {
    rx = -ry - rz;
  } else if (dy > dz) {
    ry = -rx - rz;
  } else {
    rz = -rx - ry;
  }

  return { x: rx, y: ry, z: rz };
}

/** Convert a pixel position back to the nearest hex coordinate. Uses cube rounding for accuracy. */
export function pixelToHex(pos: Vec2, cellSize: number): HexCoord {
  // Reverse the hexToPixel formula to get fractional offset coords
  const fR = pos.y / (cellSize * SQRT3);
  const r = Math.round(fR);
  const fQ = (pos.x / cellSize - (r & 1)) / 2;

  // Convert fractional offset to fractional cube for rounding
  const fCubeX = fQ - ((fR - (fR % 2 === 0 ? 0 : 1)) / 2);
  // More precise: convert using the continuous fractional row
  const fx = pos.x / (cellSize * 2) - fR / 2;
  const fz = fR;
  const fy = -fx - fz;

  const cube = cubeRound(fx, fy, fz);
  return cubeToOffset(cube);
}

/** Neighbor direction offsets for odd rows (r & 1 === 1). */
const ODD_ROW_DIRS: ReadonlyArray<readonly [number, number]> = [
  [-1, 0],
  [0, -1],
  [1, -1],
  [1, 0],
  [1, 1],
  [0, 1],
];

/** Neighbor direction offsets for even rows (r & 1 === 0). */
const EVEN_ROW_DIRS: ReadonlyArray<readonly [number, number]> = [
  [-1, 0],
  [-1, -1],
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 1],
];

/** Get the 6 hex neighbors of a given coordinate. */
export function hexNeighbors(coord: HexCoord): HexCoord[] {
  const dirs = (coord.r & 1) === 1 ? ODD_ROW_DIRS : EVEN_ROW_DIRS;
  return dirs.map(([dq, dr]) => ({ q: coord.q + dq!, r: coord.r + dr! }));
}

/** Compute hex distance between two coordinates (Manhattan distance in cube space / 2). */
export function hexDistance(a: HexCoord, b: HexCoord): number {
  const ac = offsetToCube(a);
  const bc = offsetToCube(b);
  return (Math.abs(ac.x - bc.x) + Math.abs(ac.y - bc.y) + Math.abs(ac.z - bc.z)) / 2;
}

/** Create a string key from a hex coordinate, suitable for Map/Set usage. */
export function coordToKey(coord: HexCoord): string {
  return `${coord.q},${coord.r}`;
}

/** Parse a string key back into a hex coordinate. */
export function keyToCoord(key: string): HexCoord {
  const parts = key.split(',');
  return { q: Number(parts[0]), r: Number(parts[1]) };
}

/** Check whether a coordinate is within valid board bounds. */
export function isValidCoord(coord: HexCoord, width: number, minRow: number = 0): boolean {
  if (coord.r < minRow) return false;
  if (coord.q < 0) return false;
  return coord.q <= width;
}
